
import { Vue, Component, Prop } from "vue-property-decorator";
import {
  required,
  maxLength,
  minLength,
  helpers
} from "vuelidate/lib/validators";
import { AmCard, AmCardBody } from "@afe/amui/src/components/Card";
import { AmTypography } from "@afe/amui/src/components/Typography";
import { AmButton } from "@afe/amui/src/components/Button";
import { AmSpacing } from "@afe/amui/src/components/Spacing";
import { AmAlert } from "@afe/amui/src/components/Alert";
import {
  FormField,
  TextField,
  PasswordField,
  Preloader,
  FormBack,
  FormError,
  FormSubmit,
  Captcha
} from "../components";
import { makeAnalytics } from "../analytics";
import AmLoginHeader from "./LoginHeader.vue";
import PhoneField from "../components/PhoneField.vue";
import SocialList from "./SocialList.vue";
import {
  companyAlias,
  loginFlow,
  LoginSubmitForm,
  LOGIN_STEPS,
  emailValidator,
  AUTH_METHODS,
  oAuthWithProvider,
  defaultSubmitFlow
} from "@/forms/service";
import { SocialRegisterTypes } from "@/api/uas";

const password = helpers.regex(
  "password",
  /^(?=.*[a-z0-9])[\w~!@#$%^&*()-_-+=/{}[]|`.,?"'<>:;[A-Z]\d]$/
);
Component.registerHooks(["validations"]);
@Component({
  components: {
    AmAlert,
    AmCard,
    AmTypography,
    AmCardBody,
    AmSpacing,
    AmButton,
    Captcha,
    FormField,
    TextField,
    PasswordField,
    Preloader,
    FormBack,
    FormError,
    FormSubmit,
    AmLoginHeader,
    PhoneField,
    SocialList
  }
})
export default class extends Vue {
  @Prop(Object) readonly config!: any;
  @Prop(Object) readonly formData!: any;
  @Prop({ default: () => loginFlow }) readonly submitForm!: LoginSubmitForm;
  @Prop(Array) readonly methods!: { label: string; value: string }[];
  @Prop(String) readonly method!: string;

  companyId: number | undefined = undefined;
  email = "";
  phone_number = "";
  password = "";
  loginUrl = "";
  recaptcha = "";
  loading = false;
  errorCode: string | null = null;
  throttleDelay = null;
  isNewDashboard = false;
  analytics = makeAnalytics();
  recaptchaEnabled = !!CONFIG.siteKey;
  AUTH_METHODS = AUTH_METHODS;

  entity_type = "";

  errors = {
    fields: {
      phone_number: []
    }
  };

  mounted() {
    const currentUrl = new URL(document.location.href);
    const company = currentUrl.searchParams.get("company");

    this.config.fields.phone_number.validationRules.errorsRule.invalid = this.config.fields.phone_number.validationRules.errorsRule.noMatch;

    if (company) {
      this.companyId = companyAlias[company];
    }
  }

  get needCaptcha() {
    return this.recaptchaEnabled && this.recaptcha === "";
  }

  get phoneError() {
    return (this.errors.fields && this.errors.fields.phone_number[0]) || "";
  }

  onVerify(code: string) {
    this.recaptcha = code;
  }

  onExpired() {
    this.recaptcha = "";
  }

  onSubmit() {
    this.errors = {
      fields: {
        phone_number: []
      }
    };

    this.$v.$touch();

    if (this.$v.$invalid) {
      return;
    }

    this.loading = true;
    this.errorCode = null;
    this.throttleDelay = null;

    const formData: {
      password: string;
      recaptcha: string;
      company_id?: number;
      email?: string;
      phone_number?: string;
    } = {
      password: this.password,
      recaptcha: this.recaptcha,
      company_id: this.companyId
    };

    if (this.method === AUTH_METHODS.PHONE) {
      formData.phone_number = this.phone_number;
    } else {
      formData.email = this.email;
    }

    this.submitForm(formData)
      .then(({ next, data }) => {
        if (next === LOGIN_STEPS.VERIFY_STEP) {
          this.$emit("verifyStep", { ...formData, ...data });
        }

        if (next === LOGIN_STEPS.ONE_TIME_PASSWORD_STEP) {
          this.$emit("otpStep", { ...formData, otp: data });
        }

        if (next === LOGIN_STEPS.REDIRECT_STEP) {
          this.analytics.gtm.eventCall("UserID", {
            UserID: (data && data.user_id) || ""
          });
          this.analytics.gtm.eventCall("Login Success", {
            eventTimeout: 2000,
            eventCallback: () => {
              setTimeout(() => {
                this.$emit("redirect", data);
              }, 1000);
            }
          });
        }
      })
      .catch(({ errors, throttleDelay }) => {
        if (!errors) {
          this.errorCode = "unknown_error";
        } else {
          this.throttleDelay = throttleDelay || 0;
          this.errorCode = (errors && errors.code) || null;
          this.errors = errors;
        }
        this.loading = false;
      });
  }

  async socialSignup(entity_type: SocialRegisterTypes) {
    if (this.loading) {
      return;
    }

    this.entity_type = entity_type;
    this.loading = true;
    this.errorCode = "";
    try {
      const { entity } = await oAuthWithProvider(entity_type);
      const formData = { entity_type, entity };
      defaultSubmitFlow(formData)
        .then(({ next, data }) => {
          if (next === LOGIN_STEPS.ONE_TIME_PASSWORD_STEP) {
            this.analytics.gtm.eventCall("UserID", {
              UserID: (data && data.profile_id) || ""
            });
            this.$emit("changeFlow", entity_type.toUpperCase());
            this.$emit("otpStep", { ...formData, otp: data });
          }
          if (next === LOGIN_STEPS.REDIRECT_STEP) {
            this.analytics.gtm.eventCall("UserID", {
              UserID: (data && data.user_id) || ""
            });
            this.analytics.gtm.eventCall("Login Success", {});
            this.$emit("redirect", data);
          }
        })
        .catch(error => {
          this.loading = false;
          this.errorCode = error.errors.code;
          this.errors = error.errors;
        });
    } catch (error) {
      this.loading = false;
      this.errorCode = error && error.code;
    }
  }

  onRestoreStep() {
    this.$emit("restoreStep", this.email);
  }

  validations() {
    const validations = {
      email: {},
      phone_number: {},
      password: {
        required,
        noMatch: password,
        minLength: minLength(6),
        maxLength: maxLength(128)
      }
    };

    if (this.method === AUTH_METHODS.PHONE) {
      validations.phone_number = {
        minLength: minLength(8),
        maxLength: maxLength(15),
        required: required()
      };
      validations.email = {};
    }

    if (this.method === AUTH_METHODS.EMAIL) {
      validations.phone_number = {};
      validations.email = {
        maxLength: maxLength(255),
        minLength: minLength(4),
        noMatch: emailValidator,
        required: required()
      };
    }

    return validations;
  }

  get entityNotLinkedError() {
    return this.config.social.social_login_no_linked.replaceAll(
      "{entity_type}",
      this.entity_type
    );
  }

  get socialProviderFailError() {
    return this.config.social.social_login_error.replaceAll(
      "{entity_type}",
      this.entity_type
    );
  }
}
